import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';

/**
 * Sign up new user with email and password
 * @param {string} email
 * @param {string} password
 * @param {string} fullName
 * @param {string|null} testAccountCode
 * @param {'client'|'expert'} role
 * @param {{ title?: string, bio?: string, specialties?: string[], years_experience?: number }|null} expertData  - only for experts
 */
export const signUp = async (email, password, fullName, testAccountCode = null, role = 'client', expertData = null) => {
  try {
    const { data: authData, error: authError } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
          role,
          test_account_code: testAccountCode,
          // Expert fields stored in metadata so the DB trigger can create expert_profiles
          ...(role === 'expert' && expertData ? {
            expert_title: expertData?.title || null,
            expert_bio: expertData?.bio || null,
            expert_specialties: expertData?.specialties?.length ? expertData.specialties : [],
            expert_years_experience: expertData?.years_experience || null,
            expert_hourly_rate: expertData?.hourly_rate || null,
          } : {})
        }
      }
    });

    if (authError) throw authError;

    // Log signup
    await logAuditEvent({
      actionType: 'signup',
      actionDescription: `New ${role} registered: ${email}${testAccountCode ? ' (test account requested)' : ''}`,
      severity: 'info'
    });

    // Notify admin when a new expert signs up
    if (role === 'expert') {
      supabase?.functions?.invoke('send-expert-signup-notification', {
        body: {
          expertName: fullName,
          expertEmail: email,
          title: expertData?.title || null,
          specialties: expertData?.specialties || [],
          yearsExperience: expertData?.years_experience ?? null,
        }
      }).catch((err) => console.warn('Admin expert signup notification failed:', err));
    }

    return { data: authData, error: null };
  } catch (error) {
    console.error('Signup error:', error);
    return { data: null, error };
  }
};

/**
 * Sign in user with email and password
 */
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password
    });

    if (error) {
      // Log failed login
      await logAuditEvent({
        actionType: 'login_failed',
        actionDescription: `Failed login attempt for ${email}`,
        severity: 'warning'
      });
      throw error;
    }

    // Log successful login
    await logAuditEvent({
      actionType: 'login',
      actionDescription: 'User logged in successfully',
      severity: 'info'
    });

    return { data, error: null };
  } catch (error) {
    console.error('Sign in error:', error);
    return { data: null, error };
  }
};

/**
 * Sign out user
 */
export const signOut = async () => {
  try {
    // Log logout before signing out
    await logAuditEvent({
      actionType: 'logout',
      actionDescription: 'User logged out',
      severity: 'info'
    });

    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Sign out error:', error);
    return { error };
  }
};

/**
 * Reset password for user
 */
export const resetPassword = async (email) => {
  try {
    const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
      redirectTo: `${window.location?.origin}/reset-password`
    });

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Reset password error:', error);
    return { error };
  }
};

/**
 * Update password for user
 */
export const updatePassword = async (newPassword) => {
  try {
    const { error } = await supabase?.auth?.updateUser({
      password: newPassword
    });

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Update password error:', error);
    return { error };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// TOTP helpers (RFC 6238) — browser-native Web Crypto, no extra dependencies
// ─────────────────────────────────────────────────────────────────────────────

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

function base32Encode(bytes) {
  let bits = 0, value = 0, result = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      result += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) result += BASE32_ALPHABET[(value << (5 - bits)) & 31];
  return result;
}

function base32Decode(base32) {
  const clean = base32.toUpperCase().replace(/=+$/, '').replace(/\s/g, '');
  const output = [];
  let bits = 0, value = 0;
  for (const char of clean) {
    const idx = BASE32_ALPHABET.indexOf(char);
    if (idx === -1) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      output.push((value >>> (bits - 8)) & 255);
      bits -= 8;
    }
  }
  return new Uint8Array(output);
}

async function computeTOTP(secret, counter) {
  const keyData = base32Decode(secret);
  const counterBuf = new ArrayBuffer(8);
  const view = new DataView(counterBuf);
  // Write 64-bit big-endian counter
  view.setUint32(0, Math.floor(counter / 0x100000000), false);
  view.setUint32(4, counter >>> 0, false);

  const key = await crypto.subtle.importKey(
    'raw', keyData, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']
  );
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, counterBuf));
  const offset = sig[sig.length - 1] & 0x0f;
  const code = (
    ((sig[offset]     & 0x7f) << 24) |
    ((sig[offset + 1] & 0xff) << 16) |
    ((sig[offset + 2] & 0xff) <<  8) |
     (sig[offset + 3] & 0xff)
  ) % 1_000_000;
  return String(code).padStart(6, '0');
}

/**
 * Constant-time string comparison (H-02).
 * Prevents timing side-channels by ensuring the comparison always visits
 * every character regardless of where the first difference occurs.
 * Returns true only when both strings are identical in length and content.
 */
function timingSafeEqual(a, b) {
  const enc = new TextEncoder();
  const bufA = enc.encode(a);
  const bufB = enc.encode(b);
  // Always run all XOR operations even when lengths differ, to avoid leaking
  // length information through short-circuit returns.
  let diff = bufA.length ^ bufB.length;           // non-zero if lengths differ
  const len = Math.max(bufA.length, bufB.length);
  for (let i = 0; i < len; i++) {
    diff |= (bufA[i] ?? 0) ^ (bufB[i] ?? 0);
  }
  return diff === 0;
}

async function checkTOTP(secret, token, windowSize = 1) {
  const counter = Math.floor(Date.now() / 1000 / 30);
  // Evaluate ALL windows before returning to avoid leaking which window matched.
  let matched = false;
  for (let i = -windowSize; i <= windowSize; i++) {
    const candidate = await computeTOTP(secret, counter + i);
    if (timingSafeEqual(candidate, token)) matched = true;
  }
  return matched;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public exports
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generate a new TOTP secret for the user and persist it (not yet enabled).
 * Calls the setup-2fa Edge Function so the write uses service-role (C-05).
 * Returns { success, secret, otpauthUrl }
 */
export async function generate2FASecret(userId, email) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const jwt = session?.access_token;
    if (!jwt) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.functions.invoke('setup-2fa', {
      body: { action: 'generate' },
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (error) throw error;
    return data ?? { success: false, error: 'No response from server' };
  } catch (error) {
    console.error('generate2FASecret error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Mark 2FA as enabled for the user and store backup codes.
 * Calls the setup-2fa Edge Function so the write uses service-role (C-05).
 * The verificationToken (6-digit code) is verified server-side to confirm the
 * user has scanned the QR code correctly.
 * backupCodes must be the PLAIN codes (shown to user); they are hashed server-side (C-03).
 * Returns { success }
 */
export async function enable2FA(userId, verificationToken, backupCodes) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const jwt = session?.access_token;
    if (!jwt) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.functions.invoke('setup-2fa', {
      body: { action: 'enable', token: verificationToken, backupCodes },
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (error) throw error;
    return data ?? { success: false, error: 'No response from server' };
  } catch (error) {
    console.error('enable2FA error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Generate `count` random single-use backup codes (format: XXXXX-XXXXX).
 */
export function generateBackupCodes(count = 10) {
  const codes = [];
  for (let i = 0; i < count; i++) {
    const bytes = new Uint8Array(5);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    codes.push(`${hex.slice(0, 5)}-${hex.slice(5)}`);
  }
  return codes;
}

/**
 * Verify a TOTP code against a known secret (used during setup).
 */
export async function verifyTOTP(secret, token) {
  try {
    return await checkTOTP(secret, token);
  } catch (error) {
    console.error('verifyTOTP error:', error);
    return false;
  }
}

/**
 * Verify a 2FA code at login time — delegates entirely to the `verify-2fa`
 * Edge Function so that twofa_secret never leaves the server (C-02, C-03).
 * The `userId` parameter is kept for API compatibility but is ignored;
 * the Edge Function derives the user from the JWT.
 * Returns { success }
 */
/**
 * Verify a 2FA code at login time — delegates entirely to the `verify-2fa`
 * Edge Function so that twofa_secret never leaves the server (C-02, C-03).
 *
 * @param {string} _userId   - kept for API compatibility, ignored (server derives from JWT)
 * @param {string} token     - the TOTP or backup code entered by the user
 * @param {string} [accessToken] - the access_token from the signIn response; pass this
 *   explicitly to avoid a getSession() race right after login
 */
export async function verify2FACode(_userId, token, accessToken = null) {
  try {
    // Prefer the token passed directly from the login flow (no race condition).
    // Fall back to getSession() for any other call sites.
    let jwt = accessToken;
    if (!jwt) {
      const { data: { session } } = await supabase.auth.getSession();
      jwt = session?.access_token ?? null;
    }

    if (!jwt) {
      return { success: false, error: 'No active session — please sign in again.' };
    }

    const { data, error } = await supabase?.functions?.invoke('verify-2fa', {
      body: { token },
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (error) throw error;
    return data ?? { success: false, error: 'No response from server' };
  } catch (error) {
    console.error('verify2FACode error:', error);
    return { success: false, error: error?.message };
  }
}

export async function verifyEmail(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: verifyEmail is not implemented yet.', args);
  return null;
}

export async function resendVerificationEmail(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: resendVerificationEmail is not implemented yet.', args);
  return null;
}